import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { FiChevronRight } from 'react-icons/fi';
import type { DashboardAlert } from '../types/dashboard';
import { DASHBOARD_ALERT_TYPES } from '../constants/dashboardAlerts';
import { DASHBOARD_LAYOUT } from '../constants/dashboardLayout';
import { sortDeadlineAlerts } from '../utils/dashboardData';

interface Props {
  alerts: DashboardAlert[];
  /** Opens the period in My checklist and scrolls to it. */
  onOpenPeriod?: (reportTagId: number) => void;
}

export function deadlineRowLabel(alert: DashboardAlert): string {
  const days = alert.days_until_deadline;
  if (alert.type === DASHBOARD_ALERT_TYPES.DEADLINE_PASSED) {
    if (days == null) return 'Past deadline';
    const lateBy = Math.abs(days);
    if (lateBy === 0) return 'Late today';
    return `${lateBy} day${lateBy === 1 ? '' : 's'} late`;
  }
  if (days == null) return 'Due soon';
  if (days === 0) return 'Due today';
  return `Due in ${days} day${days === 1 ? '' : 's'}`;
}

export function deadlinesSummary(alerts: DashboardAlert[]): string {
  const overdue = alerts.filter((a) => a.type === DASHBOARD_ALERT_TYPES.DEADLINE_PASSED).length;
  const dueSoon = alerts.filter((a) => a.type === DASHBOARD_ALERT_TYPES.DEADLINE_URGENT).length;
  const parts: string[] = [];
  if (overdue > 0) parts.push(`${overdue} overdue`);
  if (dueSoon > 0) parts.push(`${dueSoon} due soon`);
  return parts.join(' · ');
}

const DeadlinesCard: React.FC<Props> = ({ alerts, onOpenPeriod }) => {
  const [showAll, setShowAll] = useState(false);
  const sorted = sortDeadlineAlerts(alerts);

  if (!sorted.length) return null;

  const visible = showAll ? sorted : sorted.slice(0, DASHBOARD_LAYOUT.DEADLINES_PREVIEW);
  const hidden = sorted.length - visible.length;

  return (
    <Card className="ntcog-card dashboard-section-card dashboard-deadlines-card">
      <Card.Header>
        <div>Deadlines</div>
        <small className="text-muted fw-normal d-block">{deadlinesSummary(sorted)}</small>
      </Card.Header>
      <Card.Body className="p-0">
        <ul className="dashboard-deadlines list-unstyled mb-0">
          {visible.map((alert, idx) => {
            const overdue = alert.type === DASHBOARD_ALERT_TYPES.DEADLINE_PASSED;
            const tagId = alert.report_tag_id;
            return (
              <li key={`${alert.type}-${tagId ?? idx}`}>
                <button
                  type="button"
                  className="dashboard-deadlines__row w-100 border-0 bg-transparent text-start"
                  onClick={() => {
                    if (tagId != null && onOpenPeriod) onOpenPeriod(tagId);
                  }}
                >
                  <span
                    className={`dashboard-deadlines__dot dashboard-deadlines__dot--${overdue ? 'danger' : 'warning'}`}
                    aria-hidden="true"
                  />
                  <span className="dashboard-deadlines__name min-w-0">
                    {alert.period_name ?? 'Reporting period'}
                  </span>
                  <span
                    className={`dashboard-deadlines__when ${overdue ? 'text-danger-emphasis' : 'text-warning-emphasis'}`}
                  >
                    {deadlineRowLabel(alert)}
                  </span>
                  <FiChevronRight className="flex-shrink-0 text-muted" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
        {hidden > 0 ? (
          <button
            type="button"
            className="btn btn-link btn-sm dashboard-deadlines__more"
            onClick={() => setShowAll(true)}
          >
            + {hidden} more
          </button>
        ) : null}
      </Card.Body>
    </Card>
  );
};

export default DeadlinesCard;
